import React, { useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { selectActiveDay, selectFocusedUserId, updateMovement } from '@features/whereabouts/store/editMovements.duck';
import { BodyRegular } from "../../../atoms/fonts/Body";
import { Column, SpaceBetweenRow } from "../../../atoms/StructuralLayout";
import { Moment } from "moment";
import { EditHotDeskingButton } from "./EditHotDeskingButton";
import { MovementImage } from "../../../atoms/MovementImage";
import { OfficeEntity } from '@features/desks/services/AdvanceHotDeskingService';
import { FreeBusyStatus, OFFICE_OPTION } from '@features/whereabouts/services/WhereaboutOptions';
import { selectIconPack } from '@shared/store/auth.duck';
import { iconPackPath } from '@shared/utils/WhereaboutsHelper';
import { useTranslation } from "react-i18next";
import { selectAllWhereaboutsOptions } from '@features/whereabouts/store/whereaboutsOptions.duck';
import { DialogIdentifiers, openDialogWithPayload } from '@shared/store/dialog.duck';
import { addNewNote } from '@features/whereabouts/store/dayNotes.duck';
import { DATE_FORMAT } from '@shared/utils/DateUtils';
import { RecentNotes } from "./RecentNotes";
import { DayNotesListContainer } from "./DayNotesActivityItem";
import { AddNoteButton } from "./AddNoteButton";
import { ButtonWrapper } from "./ButtonWrapper";

interface Props {
  activeDate: Moment;
  offices: OfficeEntity[];
}

export default function ActivitySimpleOffice(props: Props) {
  const dispatch = useDispatch();
  const iconPack = useSelector(selectIconPack);
  const {t} = useTranslation();
  const allWhereaboutsOptions = useSelector(selectAllWhereaboutsOptions);
  const activeDay = useSelector(selectActiveDay);
  const activeUserId = useSelector(selectFocusedUserId);

  const officeOption = useMemo(() => {
    return allWhereaboutsOptions.find(o => o.key === 'OFFICE') ?? OFFICE_OPTION;
  }, [allWhereaboutsOptions]);

  const updateSelectedDay = (e: Event) => {
    e.stopPropagation();
    dispatch(updateMovement({selectedOption: OFFICE_OPTION, locationId: 0}));
  }

  const onNoteEnteredOrSelected = useCallback((e: Event, note: string) => {
    dispatch(updateMovement({selectedOption: OFFICE_OPTION, locationId: 0, activeDay: activeDay?.format(DATE_FORMAT), activeUserId, dayNote: note}));
    dispatch(addNewNote({noteToAdd: note, optionId: officeOption.id}));
  }, [dispatch, activeDay, activeUserId, officeOption.id]);

  const openAddNoteDialog = (e: any) => {
    e.stopPropagation();
    dispatch(openDialogWithPayload({
      payload: { onSaveClicked: onNoteEnteredOrSelected, whereabouts: officeOption },
      activeDialog: DialogIdentifiers.DayNotesEntryDialog
    }));
  }

  return <div className="activity" onClick={(e: any) => updateSelectedDay(e)} data-test="activity-movement-icon-office">
    <MovementImage src={iconPackPath(iconPack, 'Office.svg', FreeBusyStatus.FREE)} alt="At the office"/>
    <SpaceBetweenRow style={{padding: 0, alignItems: 'center'}}>
      <Column>
        <BodyRegular weight={600}>{t('whereabouts-labels.OFFICE')}</BodyRegular>
      </Column>
      <ButtonWrapper>
        {officeOption.dayNotesEnabled && <AddNoteButton onClick={openAddNoteDialog} />}
        {props.offices.length > 0 && <EditHotDeskingButton/>}
      </ButtonWrapper>
    </SpaceBetweenRow>
    {officeOption.dayNotesEnabled && <DayNotesListContainer>
      <RecentNotes whereaboutsOptionId={officeOption.id}
                   onNoteSelected={onNoteEnteredOrSelected} />
    </DayNotesListContainer>}
  </div>
}
